import React, { useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSepaExportBatches } from "../../../../../stores/Sepa/sepa.queries";
import {
  useUndoSepaExportBatch,
  useMarkSepaExportSent,
  useMarkSepaExportAccepted,
  useMarkSepaExportRejected,
} from "../../../../../stores/Sepa/sepa.mutations";
import { SepaExportBatchRow } from "../../../../../stores/Sepa/sepa.types";
import { useDownload } from "../../../../../hooks/useDownload";
import { WaitingBackdrop } from "../../../Base/components/BackDrop";
import SepaExportBatchTable from "./SepaExportBatchTable";
import CursorLoadMore from "./CursorLoadMore";

interface Props {
  accountId: string;
  /** Opens the export timeline dialog for a batch. */
  onOpenTimeline?: (batchId: string) => void;
  /** When set, the matching batch row is scrolled into view and briefly highlighted. */
  highlightBatchId?: string | null;
  /** Bumped on every navigate request so re-selecting the same batch re-flashes it. */
  highlightNonce?: number;
}

/**
 * "Exporte" tab: cursor-paginated list of SEPA export batches with XML/PDF download
 * and undo (returns the batch's transactions to PENDING).
 */
const SepaExportBatchTab: React.FC<Props> = ({
  accountId,
  onOpenTimeline,
  highlightBatchId,
  highlightNonce,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const query = useSepaExportBatches(
    { accountId, searchStr: submittedSearch || undefined },
    !!accountId,
  );
  const { handleDownloadPdf } = useDownload();
  const undoBatch = useUndoSepaExportBatch();
  const markSent = useMarkSepaExportSent();
  const markAccepted = useMarkSepaExportAccepted();
  const markRejected = useMarkSepaExportRejected();

  const busy =
    undoBatch.isPending || markSent.isPending || markAccepted.isPending || markRejected.isPending;

  const rows = useMemo<SepaExportBatchRow[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const [pendingUndo, setPendingUndo] = useState<SepaExportBatchRow | null>(null);
  const [pendingSent, setPendingSent] = useState<SepaExportBatchRow | null>(null);
  const [pendingAccept, setPendingAccept] = useState<SepaExportBatchRow | null>(null);
  const [pendingReject, setPendingReject] = useState<SepaExportBatchRow | null>(null);
  const [reason, setReason] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setSubmittedSearch("");
  };

  const openUndo = (batch: SepaExportBatchRow) => {
    setReason("");
    setPendingUndo(batch);
  };

  const openReject = (batch: SepaExportBatchRow) => {
    setReason("");
    setPendingReject(batch);
  };

  const confirmUndo = () => {
    if (!pendingUndo || !reason.trim()) return;
    undoBatch.mutate(
      { id: pendingUndo.id, reason: reason.trim() },
      { onSuccess: () => setPendingUndo(null) },
    );
  };

  const confirmReject = () => {
    if (!pendingReject || !reason.trim()) return;
    markRejected.mutate(
      { id: pendingReject.id, reason: reason.trim() },
      { onSuccess: () => setPendingReject(null) },
    );
  };

  const confirmSent = () => {
    if (!pendingSent) return;
    markSent.mutate(pendingSent.id, { onSuccess: () => setPendingSent(null) });
  };

  const confirmAccept = () => {
    if (!pendingAccept) return;
    markAccepted.mutate(pendingAccept.id, { onSuccess: () => setPendingAccept(null) });
  };

  return (
    <div className="space-y-2">
      <WaitingBackdrop isWorking={busy} message="Wird verarbeitet…" />

      <form onSubmit={submitSearch} className="flex items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              // Emptying the box restores the full list without needing Enter/✕.
              if (value.trim() === "") setSubmittedSearch("");
            }}
            placeholder="Export-Nr. oder Ersteller…"
            className="h-10 pl-9 pr-9 text-base"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Suche zurücksetzen"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="submit"
          className="h-10 bg-[#4189a8] text-sm font-semibold text-white hover:bg-[#367b9a]"
          disabled={searchInput.trim().length === 0}
        >
          Suchen
        </Button>
        {submittedSearch && (
          <span className="text-sm text-slate-500">Treffer für „{submittedSearch}"</span>
        )}
      </form>

      <div
        ref={scrollRef}
        className="thin-scrollbar max-h-[860px] overflow-y-auto rounded-lg border border-solid border-slate-200"
      >
        <SepaExportBatchTable
          rows={rows}
          onDownloadDocument={handleDownloadPdf}
          onUndo={openUndo}
          onMarkSent={setPendingSent}
          onMarkAccepted={setPendingAccept}
          onMarkRejected={openReject}
          onRowClick={onOpenTimeline ? (batch) => onOpenTimeline(batch.id) : undefined}
          highlightBatchId={highlightBatchId}
          highlightNonce={highlightNonce}
          busy={busy}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          bare
        />

        <CursorLoadMore
          hasNextPage={!!query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => query.fetchNextPage()}
          scrollRootRef={scrollRef}
        />
      </div>

      <AlertDialog open={!!pendingUndo} onOpenChange={(open) => !open && setPendingUndo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export rückgängig machen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingUndo
                ? `Export ${pendingUndo.batchNumber} wird zurückgerollt. Die ${pendingUndo.transactionCount} enthaltenen Transaktionen werden wieder als offen markiert. Die erzeugten Dateien bleiben zur Prüfung erhalten.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Grund für den Rollback"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={!reason.trim() || busy}
              onClick={confirmUndo}
            >
              Rückgängig machen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingSent} onOpenChange={(open) => !open && setPendingSent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Als an die Bank gesendet markieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSent
                ? `Export ${pendingSent.batchNumber} wird als an die Bank übermittelt markiert. Danach ist ein Rollback nicht mehr möglich.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction className="bg-[#223b63] hover:bg-[#24546b]" onClick={confirmSent}>
              Als gesendet markieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingAccept} onOpenChange={(open) => !open && setPendingAccept(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bankbestätigung erfassen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAccept
                ? `Export ${pendingAccept.batchNumber} wird als von der Bank bestätigt markiert. Die enthaltenen Transaktionen werden als abgerechnet markiert.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={confirmAccept}
            >
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingReject} onOpenChange={(open) => !open && setPendingReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bankablehnung erfassen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReject
                ? `Export ${pendingReject.batchNumber} wurde von der Bank abgelehnt. Die enthaltenen Transaktionen werden als zurückgegeben markiert.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Ablehnungsgrund / Rückgabecode"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={!reason.trim() || busy}
              onClick={confirmReject}
            >
              Ablehnung erfassen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SepaExportBatchTab;
